// src/pages/admin/EventDetails.js
import React, { useState, useEffect } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Grid,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Link,
} from "@mui/material";
import {
    ArrowBack as BackIcon,
    Download as DownloadIcon,
    Upload as UploadIcon,
    CheckCircle as CheckIcon,
    Cancel as CancelIcon,
    OpenInNew as OpenIcon,
    Edit as EditIcon,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import {
    getEventById,
    bulkMarkAttendance,
    uploadCertificates,
    getAttendanceSheet,
} from "../../services/eventService";
import AdminLayout from "../AdminLayout";
import * as XLSX from "xlsx";

export default function EventDetails() {
    const navigate = useNavigate();
    const { eventId } = useParams();
    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [attendance, setAttendance] = useState({});
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [manualDialogOpen, setManualDialogOpen] = useState(false);
    const [certificateFile, setCertificateFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [manualCertUrl, setManualCertUrl] = useState("");

    useEffect(() => {
        fetchEvent();
    }, [eventId]);

    const fetchEvent = async () => {
        try {
            setLoading(true);
            const data = await getEventById(eventId);
            setEvent(data);

            // Initialize attendance state
            const initialAttendance = {};
            (data?.registrations || []).forEach((reg) => {
                initialAttendance[reg.studentId] = reg.attended || false;
            });
            setAttendance(initialAttendance);
        } catch (err) {
            console.error("Error fetching event:", err);
            setError("Failed to fetch event details");
        } finally {
            setLoading(false);
        }
    };

    const handleAttendanceToggle = (studentId) => {
        setAttendance((prev) => ({
            ...prev,
            [studentId]: !prev[studentId],
        }));
    };

    const handleSaveAttendance = async () => {
        try {
            const attendanceData = Object.entries(attendance).map(
                ([studentId, attended]) => ({
                    studentId,
                    attended,
                })
            );
            await bulkMarkAttendance(eventId, attendanceData);
            setSuccess("Attendance saved successfully!");
            fetchEvent();
        } catch (err) {
            console.error("Error saving attendance:", err);
            setError("Failed to save attendance");
        }
    };

    const handleDownloadRegistrations = () => {
        const data = (event.registrations || []).map((reg) => ({
            Name: reg.name,
            "Student ID": reg.studentId,
            Email: reg.email,
            Course: reg.course,
            Year: reg.year,
            "Mobile No": reg.mobileNo,
            Attended: attendance[reg.studentId] ? "Yes" : "No",
            "Certificate URL": reg.certificateUrl || "",
        }));

        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Registrations");
        XLSX.writeFile(wb, `${event.title}_Registrations.xlsx`);
    };

    const handleDownloadAttendanceSheet = async () => {
        try {
            const data = await getAttendanceSheet(eventId);
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Attendance");
            XLSX.writeFile(wb, `${event.title}_Attendance.xlsx`);
        } catch (err) {
            console.error("Error downloading attendance:", err);
            setError("Failed to download attendance sheet");
        }
    };

    const handleCertificateUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            setCertificateFile(file);
            // Preview the file
            const reader = new FileReader();
            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet);

                    // console.log("📊 Excel preview data:", jsonData);
                    setPreviewData(jsonData);
                } catch (err) {
                    console.error("Error previewing file:", err);
                    setError("Failed to preview file. Please check the format.");
                }
            };
            reader.readAsArrayBuffer(file);
        }
    };

    const handleUploadCertificates = async () => {
        if (!certificateFile) {
            setError("Please select a file to upload");
            return;
        }

        try {
            const reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: "array" });
                    const sheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[sheetName];
                    const jsonData = XLSX.utils.sheet_to_json(worksheet);

                    // console.log("📊 Raw Excel data:", jsonData);

                    // Map with flexible column name matching
                    const certificateData = jsonData.map((row) => {
                        // Try different possible column names
                        const studentId =
                            row["studentId"] ||
                            row["Student ID"] ||
                            row["student_id"] ||
                            row["STUDENT ID"] ||
                            row["StudentId"] ||
                            "";

                        const certificateUrl =
                            row["certificateUrl"] ||
                            row["Certificate URL"] ||
                            row["certificate_url"] ||
                            row["CERTIFICATE URL"] ||
                            row["CertificateUrl"] ||
                            row["Certificate Link"] ||
                            row["Link"] ||
                            "";

                        // console.log("📝 Mapped row:", { studentId, certificateUrl });

                        return {
                            studentId: String(studentId).trim(),
                            certificateUrl: String(certificateUrl).trim(),
                        };
                    });

                    // console.log("✅ Final certificate data to upload:", certificateData);

                    // Validate data
                    const invalidRows = certificateData.filter(
                        (row) => !row.studentId || !row.certificateUrl
                    );

                    if (invalidRows.length > 0) {
                        setError(
                            `⚠️ Found ${invalidRows.length} rows with missing studentId or certificateUrl. Please check your Excel file.`
                        );
                        // console.error("❌ Invalid rows:", invalidRows);
                        return;
                    }

                    // Check which studentIds will be updated
                    const registeredStudentIds = (event.registrations || []).map(
                        (r) => String(r.studentId).trim()
                    );

                    // console.log("🎯 Registered student IDs:", registeredStudentIds);

                    const matchingIds = certificateData.filter((cert) =>
                        registeredStudentIds.includes(cert.studentId)
                    );

                    const nonMatchingIds = certificateData.filter(
                        (cert) => !registeredStudentIds.includes(cert.studentId)
                    );

                    // console.log(`✅ Matching IDs: ${matchingIds.length}`, matchingIds.map(m => m.studentId));
                    // console.log(`⚠️ Non-matching IDs: ${nonMatchingIds.length}`, nonMatchingIds.map(m => m.studentId));

                    if (matchingIds.length === 0) {
                        setError(
                            "❌ No matching student IDs found! Please verify the studentId column matches registered students."
                        );
                        return;
                    }

                    await uploadCertificates(eventId, certificateData);
                    setSuccess(
                        `✅ Certificates uploaded successfully! Updated ${matchingIds.length} certificate(s).${nonMatchingIds.length > 0
                            ? ` Note: ${nonMatchingIds.length} student ID(s) not found in registrations.`
                            : ""
                        }`
                    );
                    setUploadDialogOpen(false);
                    setCertificateFile(null);
                    setPreviewData([]);
                    fetchEvent();
                } catch (err) {
                    console.error("❌ Error processing file:", err);
                    setError(`Failed to upload certificates: ${err.message}`);
                }
            };
            reader.readAsArrayBuffer(certificateFile);
        } catch (err) {
            console.error("❌ Error uploading certificates:", err);
            setError("Failed to upload certificates");
        }
    };

    const handleManualCertificate = async () => {
        if (!selectedStudent || !manualCertUrl.trim()) {
            setError("Please enter a valid certificate URL");
            return;
        }

        try {
            const certificateData = [
                {
                    studentId: selectedStudent.studentId,
                    certificateUrl: manualCertUrl.trim(),
                },
            ];

            console.log("📝 Manual certificate upload:", certificateData);

            await uploadCertificates(eventId, certificateData);
            setSuccess("Certificate uploaded successfully!");
            setManualDialogOpen(false);
            setSelectedStudent(null);
            setManualCertUrl("");
            fetchEvent();
        } catch (err) {
            console.error("Error uploading manual certificate:", err);
            setError("Failed to upload certificate");
        }
    };

    const downloadTemplate = () => {
        const templateData = (event.registrations || []).map((reg) => ({
            studentId: reg.studentId,
            name: reg.name,
            email: reg.email,
            certificateUrl: "", // Empty for admin to fill
        }));

        const ws = XLSX.utils.json_to_sheet(templateData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Certificates");
        XLSX.writeFile(wb, `${event.title}_Certificate_Template.xlsx`);
    };

    if (loading) {
        return (
            <AdminLayout title="Event Details">
                <Typography>Loading...</Typography>
            </AdminLayout>
        );
    }

    if (!event) {
        return (
            <AdminLayout title="Event Details">
                <Typography>Event not found</Typography>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout title={event.title}>
            <Box>
                <Button
                    startIcon={<BackIcon />}
                    onClick={() => navigate("/admin/events")}
                    sx={{ mb: 3, color: "#003366" }}
                >
                    Back to Events
                </Button>

                {error && (
                    <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert severity="success" onClose={() => setSuccess("")} sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {/* Event Info Card */}
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={8}>
                                <Typography
                                    variant="h5"
                                    sx={{ fontWeight: 600, color: "#003366", mb: 2 }}
                                >
                                    {event.title}
                                </Typography>
                                <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
                                    {event.description}
                                </Typography>
                                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                                    <Chip label={`Type: ${event.eventType}`} />
                                    <Chip label={`Venue: ${event.venue}`} />
                                    <Chip
                                        label={`Date: ${event.eventDate
                                            ? new Date(event.eventDate).toLocaleDateString()
                                            : "TBA"
                                            }`}
                                    />
                                    {event.eventTime && <Chip label={`Time: ${event.eventTime}`} />}
                                    <Chip
                                        label={`Registrations: ${event.registrations?.length || 0}`}
                                        color="primary"
                                    />
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                    <Button
                                        variant="contained"
                                        startIcon={<DownloadIcon />}
                                        onClick={handleDownloadRegistrations}
                                        fullWidth
                                        sx={{ backgroundColor: "#003366" }}
                                    >
                                        Download Registrations
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<DownloadIcon />}
                                        onClick={downloadTemplate}
                                        fullWidth
                                        sx={{ backgroundColor: "#28a745" }}
                                    >
                                        Download Certificate Template
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<UploadIcon />}
                                        onClick={() => setUploadDialogOpen(true)}
                                        fullWidth
                                        sx={{ backgroundColor: "#C1272D" }}
                                    >
                                        Upload Certificates
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>

                {/* Registrations Table */}
                <Card>
                    <CardContent>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 2,
                            }}
                        >
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Registrations ({event.registrations?.length || 0})
                            </Typography>
                            <Button
                                variant="contained"
                                onClick={handleSaveAttendance}
                                sx={{ backgroundColor: "#28a745" }}
                            >
                                Save Attendance
                            </Button>
                        </Box>

                        <TableContainer component={Paper} variant="outlined">
                            <Table size="small">
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Student ID</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Course</TableCell>
                                        <TableCell>Year</TableCell>
                                        <TableCell>Mobile</TableCell>
                                        <TableCell align="center">Attended</TableCell>
                                        <TableCell>Certificate</TableCell>
                                        <TableCell>Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(event.registrations || []).map((reg) => (
                                        <TableRow key={reg.studentId} hover>
                                            <TableCell>{reg.name}</TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                                    {reg.studentId}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{reg.email}</TableCell>
                                            <TableCell>{reg.course}</TableCell>
                                            <TableCell>{reg.year}</TableCell>
                                            <TableCell>{reg.mobileNo}</TableCell>
                                            <TableCell align="center">
                                                <Checkbox
                                                    checked={attendance[reg.studentId] || false}
                                                    onChange={() => handleAttendanceToggle(reg.studentId)}
                                                    color="success"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {reg.certificateUrl ? (
                                                    <Link
                                                        href={reg.certificateUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                                                    >
                                                        <OpenIcon sx={{ fontSize: 16 }} />
                                                        View
                                                    </Link>
                                                ) : (
                                                    <Chip label="Not Issued" size="small" color="default" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setSelectedStudent(reg);
                                                        setManualCertUrl(reg.certificateUrl || "");
                                                        setManualDialogOpen(true);
                                                    }}
                                                    sx={{ color: "#C1272D" }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>

                {/* Upload Dialog */}
                <Dialog
                    open={uploadDialogOpen}
                    onClose={() => {
                        setUploadDialogOpen(false);
                        setCertificateFile(null);
                        setPreviewData([]);
                    }}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle>Upload Certificates</DialogTitle>
                    <DialogContent>
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>Excel Format Required:</strong>
                            </Typography>
                            <Typography variant="body2" component="div">
                                • Column 1: <code>studentId</code> (must match exactly)
                                <br />
                                • Column 2: <code>certificateUrl</code> (full URL to certificate)
                                <br />
                                • Download the template above for correct format
                            </Typography>
                        </Alert>

                        <input
                            type="file"
                            accept=".xlsx,.xls"
                            onChange={handleCertificateUpload}
                            style={{ marginBottom: 16 }}
                        />

                        {previewData.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    Preview ({previewData.length} rows):
                                </Typography>
                                <TableContainer
                                    component={Paper}
                                    variant="outlined"
                                    sx={{ maxHeight: 300 }}
                                >
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Student ID</TableCell>
                                                <TableCell>Certificate URL</TableCell>
                                                <TableCell>Status</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {previewData.slice(0, 10).map((row, idx) => {
                                                const studentId =
                                                    row["studentId"] ||
                                                    row["Student ID"] ||
                                                    row["student_id"] ||
                                                    "";
                                                const certUrl =
                                                    row["certificateUrl"] ||
                                                    row["Certificate URL"] ||
                                                    row["certificate_url"] ||
                                                    "";
                                                const isRegistered = (event.registrations || []).some(
                                                    (r) => String(r.studentId).trim() === String(studentId).trim()
                                                );

                                                return (
                                                    <TableRow key={idx}>
                                                        <TableCell>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ fontFamily: "monospace" }}
                                                            >
                                                                {studentId || "❌ MISSING"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    maxWidth: 200,
                                                                    overflow: "hidden",
                                                                    textOverflow: "ellipsis",
                                                                }}
                                                            >
                                                                {certUrl || "❌ MISSING"}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            {isRegistered ? (
                                                                <Chip label="✓ Match" color="success" size="small" />
                                                            ) : (
                                                                <Chip label="⚠ Not Found" color="warning" size="small" />
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setUploadDialogOpen(false);
                                setCertificateFile(null);
                                setPreviewData([]);
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleUploadCertificates}
                            variant="contained"
                            disabled={!certificateFile}
                            sx={{
                                backgroundColor: "#C1272D",
                                "&:hover": { backgroundColor: "#a01f25" },
                            }}
                        >
                            Upload
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Manual Certificate Dialog */}
                <Dialog
                    open={manualDialogOpen}
                    onClose={() => {
                        setManualDialogOpen(false);
                        setSelectedStudent(null);
                        setManualCertUrl("");
                    }}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle>
                        {selectedStudent?.certificateUrl ? "Edit" : "Add"} Certificate
                    </DialogTitle>
                    <DialogContent>
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Student: <strong>{selectedStudent?.name}</strong>
                                <br />
                                ID: <strong>{selectedStudent?.studentId}</strong>
                            </Typography>
                            <TextField
                                fullWidth
                                label="Certificate URL"
                                value={manualCertUrl}
                                onChange={(e) => setManualCertUrl(e.target.value)}
                                placeholder="https://example.com/certificate.pdf"
                                helperText="Enter the full URL to the certificate"
                            />
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            onClick={() => {
                                setManualDialogOpen(false);
                                setSelectedStudent(null);
                                setManualCertUrl("");
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleManualCertificate}
                            variant="contained"
                            sx={{
                                backgroundColor: "#C1272D",
                                "&:hover": { backgroundColor: "#a01f25" },
                            }}
                        >
                            Save
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </AdminLayout>
    );
}
